import {createLogger} from 'logger';
import {
    loadKeyset as loadKeysetServer,
    loadKeysets as loadKeysetsServer,
} from '../common/i18n/keysets/load.server';

const logger = createLogger('i18n');

export type I18nKeyMap = Record<string, string | string[]>;
export type I18nKeysets = Record<string, I18nKeyMap>;
export type I18nOptions = Record<string, string | number>;
export type I18nFunction = (keyset: string, key: string, params?: I18nOptions) => string;
export type I18nKFunction = (key: string, params?: I18nOptions) => string;

export function initI18n(keysets: I18nKeysets) {
    return function i18n(keyset: string, key: string, params: I18nOptions = {}) {
        const keyValue = keysets[keyset] && (keysets[keyset][key] as string | string[] | undefined);
        let result: string;

        if (typeof keyValue === 'undefined') {
            logger.warn(`Key value is empty: ${key} in ${keyset}`);

            return 'default' in params && typeof params.default === 'string' ? params.default : key;
        }

        if (params) {
            const count = Number(params.count);

            if (Array.isArray(keyValue)) {
                if (typeof count !== 'number') {
                    logger.warn(`Plural key has invalid \`count\` parameter: ${key} in ${keyset}`);

                    return key;
                }

                const lastNumber = count % 10;
                const lastNumbers = count % 100;
                let index;

                if (count === 0) {
                    index = 3;
                } else if (lastNumber === 1 && lastNumbers !== 11) {
                    index = 0;
                } else if (
                    lastNumber > 1 &&
                    lastNumber < 5 &&
                    (lastNumbers < 10 || lastNumbers > 20)
                ) {
                    index = 1;
                } else {
                    index = 2;
                }

                result = keyValue[index];
            } else {
                result = keyValue;
            }

            Object.keys(params).forEach((param) => {
                result = (result || '').replace(
                    new RegExp(`({{${param}}})`, 'g'),
                    String(params[param]),
                );
            });
        } else {
            result = keyValue as string;
        }

        return result;
    };
}

export async function loadKeysets(localeCode?: string) {
    return __IS_SERVER__
        ? await loadKeysetsServer(localeCode)
        : (
              await import(
                  /* webpackChunkName: "keysets/[request]" */
                  `keysets/${localeCode}.json`
              )
          )?.default;
}

export async function loadKeyset(localeCode?: string, keysetName?: string) {
    return __IS_SERVER__
        ? await loadKeysetServer(localeCode, keysetName)
        : (
              await import(
                  /* webpackChunkName: "keysets/[request]" */
                  `keysets/${localeCode}/${keysetName}.json`
              )
          )?.default;
}
